import { lazy, Suspense, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';

import ChatDrawer from '@/components/Chatbot/ChatDrawer/ChatDrawer';
import WalkMeRouteSync from '@/components/base/WalkMeRouteSync/WalkMeRouteSync';
import AdobeAnalyticsProvider from '@/context/AdobeAnalyticsProvider';
import { AuthProvider } from '@/context/AuthContext';
import { FeatureToggleProvider } from '@/context/FeatureToggleContext/FeatureToggleContext';
import { FiltersProvider } from '@/context/FiltersContext/FiltersContextProvider';
import { LocalFiltersRegistryProvider } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import Routes from './Routes';
import { QueryClientProvider } from './api/queries/QueryClientProvider';
import './aws-exports';
import awsConfig from './aws-exports';
import { registerAuthHubListener } from './context/authSessionTransitions';
import { Globals, ScrollToTop, WarningPortraitMode, WithCubeProvider } from './components/base';

import useRememberLastPath from './api/login/sideEffects/useRememberLastUserPath';
import MainteanceBanner from './components/shared/MainteanceBanner/MainteanceBanner';
import { useCognitoError } from './hooks';
import { globalHcoHcpFilters } from './models/filter/globalFilterAllowlist';

import ToastProvider from '@/storybook/components/Toast/Toast';
import './i18n';
import './styles/main.scss';

const IS_DEVELOPMENT_BUILD = import.meta.env.MODE === 'development';
const DevelopmentTools = IS_DEVELOPMENT_BUILD
  ? (() => {
      const CountryOverrideDevTools = lazy(() => import('@/dev/CountryOverrideDevTools/CountryOverrideDevTools'));
      const FeatureTogglesDevTools = lazy(() => import('@/dev/FeatureTogglesDevTools/FeatureTogglesDevTools'));
      const DeveloperToolsLauncher = lazy(
        () => import('@/dev/components/DeveloperToolsLauncher/DeveloperToolsLauncher'),
      );

      return function DevelopmentToolsView() {
        return (
          <Suspense fallback={null}>
            <CountryOverrideDevTools />
            <FeatureTogglesDevTools />
            <DeveloperToolsLauncher />
          </Suspense>
        );
      };
    })()
  : null;

Auth.configure(awsConfig);
API.configure(awsConfig);

const App = () => {
  const { rememberLastUserPath } = useRememberLastPath();

  useCognitoError();

  useEffect(() => {
    return registerAuthHubListener(rememberLastUserPath);
  }, [rememberLastUserPath]);

  return (
    <>
      <Globals />
      <ToastProvider>
        <AuthProvider>
          <ScrollToTop />
          <WalkMeRouteSync />
          <MainteanceBanner />
          <WithCubeProvider>
            <QueryClientProvider>
              <AdobeAnalyticsProvider>
                <FeatureToggleProvider>
                  <LocalFiltersRegistryProvider>
                    <FiltersProvider customFilterConfig={globalHcoHcpFilters} persist>
                      <Routes />
                      <ChatDrawer />
                    </FiltersProvider>
                  </LocalFiltersRegistryProvider>
                  {DevelopmentTools && <DevelopmentTools />}
                </FeatureToggleProvider>
              </AdobeAnalyticsProvider>
            </QueryClientProvider>
          </WithCubeProvider>
          <WarningPortraitMode />
        </AuthProvider>
      </ToastProvider>
    </>
  );
};

export default App;
